import React, { useEffect, useState } from 'react';
import generateThumbnail from '../../../../misc/generateThumbnail';
import SafeImage from '../../SafeImage';
import './VideoThumb.scss';

/**
 * Video thumbnail with a play icon overlay. Uses the given `src` if present,
 * otherwise generates a thumbnail from `mediaSrc` or `file` on mount.
 *
 * @param {Object} props
 * @param {string} [props.src] - Existing thumbnail image URL.
 * @param {File|Blob} [props.file] - Local video file to generate a thumbnail from.
 * @param {string} [props.mediaSrc] - Video URL to generate a thumbnail from (takes precedence over `file`).
 * @param {string} [props.className] - Extra class name(s) for the wrapper.
 * @param {React.CSSProperties} [props.style] - Style applied to the thumbnail image.
 * @param {React.MouseEventHandler<HTMLDivElement>} [props.onClick] - Click handler for the wrapper.
 * @returns {JSX.Element}
 */
export default function VideoThumb({src: initialSrc, file, mediaSrc, className, style, onClick}) {
    const [src, setSrc] = useState(initialSrc);

    // Only runs on mount, matching the previous componentDidMount behaviour
    useEffect(() => {
        if (src || !(file || mediaSrc)) {
            return undefined;
        }
        let mounted = true;
        generateThumbnail(mediaSrc || file)
            .then(generated => {
                if (mounted) {
                    setSrc(generated);
                }
            })
            .catch(err => console.error(err));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={['VideoThumb', className, src ? '' : 'placeholder'].join(' ')} onClick={onClick}>
            <SafeImage src={src} altSrc="/img/pixel.png" style={style} />
            <div className="icon play" />
        </div>
    );
}
